import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Modal, Paper, Popper } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { findAnchor } from '@/components/spotlight-tour';
import { BUSINESS_SWITCHER_ANCHOR } from '@/consts/businesses';
import { useBusiness } from '@/contexts/BusinessContext';
import { useIsAnnouncementBlocking } from '@/components/announcements/announcementStatus';
import BusinessMigrationBanner from './BusinessMigrationBanner';
import useNavLayoutForMigration from './useNavLayoutForMigration';

// Medidas tiradas do print da historia: o contorno verde fica colado no botao do
// seletor, com um brilho estreito, e o card comeca um pouco acima dele. O card
// tem as medidas do SpotlightTour, o tour que o painel ja usa.
const HOLE_GLOW = 4;
const CARD_WIDTH = 372;
const CARD_OFFSET = [-HOLE_GLOW * 2, 24];

// No celular o seletor mora no menu lateral, que fica montado fora da tela ate
// ser aberto. Achar o elemento nao basta: ele precisa estar visivel.
const isOnScreen = (rect) =>
  Boolean(rect) &&
  rect.width > 0 &&
  rect.height > 0 &&
  rect.right > 0 &&
  rect.bottom > 0 &&
  rect.left < window.innerWidth &&
  rect.top < window.innerHeight;

const sameRect = (a, b) =>
  a === b ||
  (Boolean(a) &&
    Boolean(b) &&
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height);

// Historia CBK-528, secao 5: o destaque fica junto ao seletor (RF-02), em
// qualquer pagina (RF-04), e impede o uso da plataforma ate o Entendi (RF-06).
// Sem o seletor na tela nada aparece: no celular o destaque surge quando a
// pessoa abre o menu, decisao de 15/10 porque a historia nao mapeou o celular.
// No desktop com menu mini ou horizontal, o menu abre no vertical para o
// seletor aparecer (useNavLayoutForMigration).
export default function BusinessMigrationNotice() {
  const theme = useTheme();
  const {
    migrationBannerBusiness,
    dismissMigrationBanner,
    isDismissingMigrationBanner,
    isLoading,
    isError,
  } = useBusiness();
  // O modal de avisos tem prioridade, como no tour de produtos.
  const isAnnouncementBlocking = useIsAnnouncementBlocking();
  const pending = Boolean(migrationBannerBusiness) && !isAnnouncementBlocking;

  useNavLayoutForMigration({
    pending,
    settled: !isLoading && !isError && !migrationBannerBusiness,
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [rect, setRect] = useState(null);
  const popperRef = useRef(null);

  // Acompanha o seletor a cada quadro: o menu do celular entra deslizando, sem
  // scroll nem resize que avisem.
  useEffect(() => {
    if (!pending) {
      setAnchorEl(null);
      setRect(null);
      return undefined;
    }

    let frame = null;
    let lastRect = null;

    const track = () => {
      const element = findAnchor(BUSINESS_SWITCHER_ANCHOR);
      const nextRect = element?.getBoundingClientRect() ?? null;
      const visibleRect = isOnScreen(nextRect) ? nextRect : null;

      if (!sameRect(visibleRect, lastRect)) {
        lastRect = visibleRect;
        setAnchorEl(visibleRect ? element : null);
        setRect(visibleRect);
      }

      frame = window.requestAnimationFrame(track);
    };

    frame = window.requestAnimationFrame(track);

    return () => window.cancelAnimationFrame(frame);
  }, [pending]);

  useEffect(() => {
    popperRef.current?.update();
  }, [rect]);

  const scrim = alpha(theme.palette.grey[900], 0.72);

  const holeSx = useMemo(() => {
    if (!rect) return null;

    return {
      position: 'fixed',
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      borderRadius: 1.5,
      border: `2px solid ${theme.palette.primary.main}`,
      boxShadow: `0 0 0 9999px ${scrim}, 0 0 0 ${HOLE_GLOW}px ${alpha(
        theme.palette.primary.main,
        0.24
      )}`,
      pointerEvents: 'none',
    };
  }, [rect, theme, scrim]);

  if (!pending || !anchorEl) {
    return null;
  }

  // O Modal fica por cima do menu do celular, que tambem e um Modal, e assume a
  // trava de foco: o Tab nao sai do card e o Esc nao fecha. Todo clique fora do
  // card cai no fundo, porque o recorte nao recebe eventos.
  return (
    <Modal open hideBackdrop disableEscapeKeyDown>
      <Box sx={{ position: 'fixed', inset: 0, outline: 'none' }} tabIndex={-1}>
        <Box aria-hidden sx={holeSx} />

        {/* Sem espaco a direita, como no menu do celular, o card desce para
            baixo do seletor. */}
        <Popper
          open
          disablePortal
          anchorEl={anchorEl}
          popperRef={popperRef}
          placement="right-start"
          popperOptions={{ strategy: 'fixed' }}
          modifiers={[
            { name: 'offset', options: { offset: CARD_OFFSET } },
            { name: 'preventOverflow', options: { padding: 16 } },
            {
              name: 'flip',
              options: { padding: 16, fallbackPlacements: ['bottom-start', 'top-start'] },
            },
          ]}
        >
          <Paper
            role="dialog"
            aria-modal="true"
            aria-labelledby="business-migration-title"
            sx={{
              p: 3,
              width: CARD_WIDTH,
              maxWidth: 'calc(100vw - 32px)',
              borderRadius: 2,
              border: `solid 1px ${theme.palette.divider}`,
              boxShadow: theme.customShadows?.z24,
            }}
          >
            <BusinessMigrationBanner
              onDismiss={dismissMigrationBanner}
              isDismissing={isDismissingMigrationBanner}
            />
          </Paper>
        </Popper>
      </Box>
    </Modal>
  );
}
